import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

interface RecoveryCodeProps {
  deviceId: string;
  onSuccess: () => void;
  onBack: () => void;
}

const RecoveryCode: React.FC<RecoveryCodeProps> = ({ deviceId, onSuccess, onBack }) => {
  const { t } = useTranslation();
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState<'code' | 'newpin'>('code');
  const [error, setError] = useState('');
  const [deviceDocId, setDeviceDocId] = useState('');

  const hashPin = (pin: string) => {
    return btoa(pin);
  };

  const verifyRecoveryCode = async () => {
    const formattedCode = recoveryCode.toUpperCase().replace(/\s/g, '');
    
    try {
      const q = query(collection(db, 'devices'), where('deviceId', '==', deviceId));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setError(t('deviceLock.deviceNotFound'));
        return;
      }

      const deviceDoc = snapshot.docs[0];
      const device = deviceDoc.data();

      if (device.recoveryCode === formattedCode) {
        setDeviceDocId(deviceDoc.id);
        setError('');
        setStep('newpin');
      } else {
        setError(t('deviceLock.invalidRecoveryCode'));
      }
    } catch (error) {
      console.error('Error verifying recovery code:', error);
      setError(t('deviceLock.verificationError'));
    }
  };

  const resetPIN = async () => {
    if (newPin !== confirmPin) {
      setError(t('deviceLock.pinMismatch'));
      return;
    }

    if (newPin.length !== 6) {
      setError(t('deviceLock.pinLength'));
      return;
    }

    try {
      await updateDoc(doc(db, 'devices', deviceDocId), {
        pinHash: hashPin(newPin),
        status: 'active',
        updatedAt: Timestamp.now()
      });

      onSuccess();
    } catch (error) {
      console.error('Error resetting PIN:', error);
      setError(t('deviceLock.resetError'));
    }
  };

  if (step === 'newpin') {
    return (
      <div className="card-dark p-8 max-w-md mx-auto">
        <div className="text-center mb-6">
          <div className="text-5xl mb-4">🔑</div>
          <h3 className="text-2xl font-bold text-white mb-2">{t('deviceLock.setNewPIN')}</h3>
          <p className="text-[#B8B8C3]">{t('deviceLock.setNewPINDesc')}</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
              {t('deviceLock.newPIN')}
            </label>
            <input
              type="password"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="input-dark w-full text-center text-2xl tracking-widest"
              placeholder="••••••"
              maxLength={6}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
              {t('deviceLock.confirmNewPIN')}
            </label>
            <input
              type="password"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="input-dark w-full text-center text-2xl tracking-widest"
              placeholder="••••••"
              maxLength={6}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}

          <button
            onClick={resetPIN}
            disabled={newPin.length !== 6 || confirmPin.length !== 6}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('deviceLock.resetPIN')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card-dark p-8 max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="text-5xl mb-4">🔐</div>
        <h3 className="text-2xl font-bold text-white mb-2">{t('deviceLock.enterRecoveryCode')}</h3>
        <p className="text-[#B8B8C3]">{t('deviceLock.enterRecoveryCodeDesc')}</p>
      </div>

      <div className="space-y-4">
        <div>
          <input
            type="text"
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
            className="input-dark w-full text-center text-xl tracking-wider font-mono"
            placeholder="XXXX-XXXX-XXXX-XXXX"
            maxLength={19}
          />
          <p className="text-xs text-[#B8B8C3] mt-2 text-center">
            {t('deviceLock.recoveryCodeHint')}
          </p>
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-400 text-sm text-center">{error}</p>
          </div>
        )}

        <button
          onClick={verifyRecoveryCode}
          disabled={recoveryCode.length < 16}
          className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('deviceLock.verify')}
        </button>

        <button
          onClick={onBack}
          className="btn-secondary w-full"
        >
          {t('common.back')}
        </button>
      </div>
    </div>
  );
};

export default RecoveryCode;
